<!-- src/apps/secret/components/dashboard/SecretComposer.vue -->

<script setup lang="ts">
  import AttachmentPicker from '@/apps/secret/components/form/AttachmentPicker.vue';
  import {
    COUNTRY_CODES,
    DEFAULT_COUNTRY,
    type CountryCode,
  } from '@/apps/secret/components/receipt/country-codes';
  import {
    storeAttachmentFragment,
    peekAttachmentFragment,
  } from '@/apps/secret/composables/useAttachmentFragmentStore';
  import {
    useAttachments,
    type UploadedAttachment,
  } from '@/apps/secret/composables/useAttachments';
  import { DEFAULT_SECRET_MAX_LENGTH } from '@/schemas/contracts/bootstrap';
  import { sendWhatsAppLink } from '@/services/whatsapp.service';
  import BasicFormAlerts from '@/shared/components/forms/BasicFormAlerts.vue';
  import CornerBrackets from '@/shared/components/ui/CornerBrackets.vue';
  import { useClipboard } from '@/shared/composables/useClipboard';
  import { usePrivacyOptions } from '@/shared/composables/usePrivacyOptions';
  import { useSecretConcealer } from '@/shared/composables/useSecretConcealer';
  import { useBootstrapStore } from '@/shared/stores/bootstrapStore';
  import { useLocalReceiptStore } from '@/shared/stores/localReceiptStore';
  import { useNotificationsStore } from '@/shared/stores/notificationsStore';
  import { type LocalReceipt } from '@/types/ui/local-receipt';
  import { onClickOutside, useMediaQuery } from '@vueuse/core';
  import { nanoid } from 'nanoid';
  import { storeToRefs } from 'pinia';
  import { computed, nextTick, ref } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useRouter } from 'vue-router';

  import DashboardPrivacyBar from './DashboardPrivacyBar.vue';

  /**
   * Authenticated dashboard composer (Figma Make, ago/2026).
   *
   * Deliberately NOT a variant of SecretForm: the mock pulls the passphrase and
   * expiry controls OUT of the panel into a toolbar and swaps the SplitButton
   * for a single "create link" action plus an inline generated-link state.
   * Expressing that through more SecretForm props would have grown an already
   * 600-line component; instead this composes the SAME composables SecretForm
   * uses (useSecretConcealer + usePrivacyOptions), so no creation logic is
   * duplicated — only the chrome differs. This replaced WorkspaceSecretForm,
   * pattern.
   */

  const props = withDefaults(
    defineProps<{
      /** Stay on the page after creating (localReceiptStore.workspaceMode) */
      workspaceMode?: boolean;
    }>(),
    { workspaceMode: false }
  );

  const emit = defineEmits<{
    (e: 'created', receipt: LocalReceipt): void;
  }>();

  const { t, locale } = useI18n();
  const router = useRouter();
  const bootstrapStore = useBootstrapStore();
  const localReceiptStore = useLocalReceiptStore();

  const {
    secret_options: secretOptions,
    uiCapabilities,
    link_host: linkHost,
  } = storeToRefs(bootstrapStore);

  // --- E2EE file attachments (same flow as SecretForm) -----------------------
  const {
    uploadAttachment,
    buildAttachmentFragment: buildAttachmentFragmentFn,
  } = useAttachments();
  const attachmentsConfig = computed(
    () =>
      (secretOptions.value as { attachments?: { enabled?: boolean; max_size?: number } } | undefined)
        ?.attachments
  );
  const attachmentsEnabled = computed(() => attachmentsConfig.value?.enabled === true);
  const attachmentMaxSize = computed(() => Number(attachmentsConfig.value?.max_size) || 26_214_400);
  const selectedFiles = ref<File[]>([]);
  const attachmentProgress = ref<number[]>([]);
  const uploadingAttachments = ref(false);
  // Upload-failure surfacing + retry-to-the-same-receipt (the secret already
  // exists; only the file upload failed, so we re-run it against that receipt).
  const notifications = useNotificationsStore();
  const attachmentError = ref('');
  const retryReceiptId = ref('');

  async function retryAttachmentUpload(): Promise<void> {
    if (!retryReceiptId.value || !selectedFiles.value.length) return;
    attachmentError.value = '';
    try {
      const fragment = await uploadSelectedAttachments(retryReceiptId.value);
      selectedFiles.value = [];
      lastReceiptHasAttachment.value = !!fragment;
      if (fragment) storeAttachmentFragment(retryReceiptId.value, fragment);
      retryReceiptId.value = '';
    } catch (err) {
       
      console.error('[attachments] retry failed', err);
      attachmentError.value = t('web.homepage.attach_upload_failed');
      notifications.show(t('web.homepage.attach_upload_failed'), 'error', 'top');
    }
  }

  // Encrypt + upload the selected files to the fresh receipt; returns the id→key
  // fragment to append to the share link. Authenticated (dashboard), so never the
  // guest route. Never throws into the creation flow.
  async function uploadSelectedAttachments(receiptId: string): Promise<string> {
    if (!attachmentsEnabled.value || !selectedFiles.value.length) return '';
    uploadingAttachments.value = true;
    attachmentProgress.value = selectedFiles.value.map(() => 0);
    try {
      const uploaded: UploadedAttachment[] = [];
      for (let i = 0; i < selectedFiles.value.length; i++) {
        const record = await uploadAttachment(receiptId, selectedFiles.value[i], {
          isGuest: false,
          onProgress: (loaded, total) => {
            attachmentProgress.value[i] = total > 0 ? (loaded / total) * 100 : 0;
          },
        });
        attachmentProgress.value[i] = 100;
        uploaded.push(record);
      }
      return buildAttachmentFragmentFn(uploaded);
    } finally {
      uploadingAttachments.value = false;
    }
  }

  // The operator-configured cap (site.secret_options.content.maximum_length,
  // env SECRET_MAX_LENGTH). Never raise it here — the server rejects anything
  // longer, so the counter must show the same ceiling the backend enforces.
  const maxLength = computed(
    () => secretOptions.value?.content?.maximum_length ?? DEFAULT_SECRET_MAX_LENGTH
  );

  const textareaRef = ref<HTMLTextAreaElement | null>(null);
  const recipientRef = ref<HTMLInputElement | null>(null);
  const lastReceipt = ref<LocalReceipt | null>(null);
  // Drives the "copy now" warning: a link with an attachment carries the E2EE
  // key only in its fragment, so it can't be rebuilt once this tab forgets it.
  const lastReceiptHasAttachment = ref(false);

  const { form, validation, operations, isSubmitting, submit } = useSecretConcealer({
    onSuccess: async (response) => {
      if (!response) throw new Error('Response is missing');
      const subjectValue = (form.subject as string) || undefined;
      const receipt: LocalReceipt = {
        id: nanoid(),
        receiptExtid: response.record.receipt.identifier,
        receiptShortid: response.record.receipt.shortid,
        secretExtid: response.record.secret.identifier,
        secretShortid: response.record.secret.shortid,
        shareDomain: response.record.share_domain,
        hasPassphrase: !!form.passphrase,
        ttl: form.ttl as number,
        createdAt: Date.now(),
        subject: subjectValue,
        deliveryMethod: shareType.value,
      };
      localReceiptStore.addReceipt(receipt);

      // Upload any E2EE attachments to the fresh receipt and get the id→key
      // fragment. A FAILED upload must NOT be silent: surface it, keep the
      // selection, and remember the receipt so the user can retry the upload
      // (the secret itself already exists).
      let attachmentFragment = '';
      attachmentError.value = '';
      let attachmentOk = true;
      try {
        attachmentFragment = await uploadSelectedAttachments(receipt.receiptExtid);
      } catch (err) {
         
        console.error('[attachments] upload failed', err);
        attachmentOk = false;
        attachmentError.value = t('web.homepage.attach_upload_failed');
        retryReceiptId.value = receipt.receiptExtid;
        notifications.show(t('web.homepage.attach_upload_failed'), 'error', 'top');
      }
      if (attachmentOk) selectedFiles.value = [];
      lastReceiptHasAttachment.value = !!attachmentFragment;
      if (attachmentFragment) storeAttachmentFragment(receipt.receiptExtid, attachmentFragment);

      // WhatsApp/Telegram are "create then open": the secret only exists after
      // creation, so we build its link from the response and hand off to the
      // user's own app (client-side, no paid provider). Same behaviour as the
      // receipt page's ShareChannels. The attachment fragment (E2EE keys) rides
      // in the URL hash so the recipient can decrypt.
      const domain = receipt.shareDomain || linkHost.value;
      const hash = attachmentFragment ? `#${attachmentFragment}` : '';
      const createdShareUrl = `https://${domain}/secret/${receipt.secretExtid}${hash}`;
      if (shareType.value === 'whatsapp') {
        const result = sendWhatsAppLink({
          countryCode: selectedCountry.value.dial,
          number: waNumber.value,
          secretUrl: createdShareUrl,
        });
        if (result.waMeUrl) window.open(result.waMeUrl, '_blank', 'noopener');
      } else if (shareType.value === 'telegram') {
        window.open(
          `https://t.me/share/url?url=${encodeURIComponent(createdShareUrl)}`,
          '_blank',
          'noopener'
        );
      }

      // TTL and passphrase are toolbar settings, not per-secret input: keep
      // them across submissions the way the mock's toolbar implies.
      const keptTtl = form.ttl as number;
      const keptPassphrase = form.passphrase as string;
      operations.reset();
      operations.updateField('ttl', keptTtl);
      operations.updateField('passphrase', keptPassphrase);
      // Restore the delivery selection so the panel doesn't jump back to "link"
      // after each create.
      operations.updateField('delivery_method', shareType.value);

      emit('created', receipt);

      if (props.workspaceMode) {
        lastReceipt.value = receipt;
      } else {
        router.push(`/receipt/${receipt.receiptExtid}`);
      }
    },
  });

  const { lifetimeOptions, updatePassphrase, updateTtl } = usePrivacyOptions(operations);

  // Restore the user's last-used TTL when it is still an offered option,
  // sticky expiry: the chosen TTL survives creating a secret.
  const preferredTtl = localReceiptStore.preferredTtl;
  if (preferredTtl != null && lifetimeOptions.value.some((o) => o.value === preferredTtl)) {
    operations.updateField('ttl', preferredTtl);
  }

  const onTtlChange = (value: number) => {
    updateTtl(value);
    localReceiptStore.setPreferredTtl(value);
  };

  const secretContent = computed({
    get: () => (form.secret as string) ?? '',
    set: (value: string) => operations.updateField('secret', value),
  });

  const charCount = computed(() => secretContent.value.length);
  const isNearLimit = computed(() => charCount.value > maxLength.value * 0.85);
  const hasContent = computed(() => secretContent.value.trim().length > 0);
  // ASSUNTO is required: every message must carry a label so it is
  // identifiable in the messages ledger.
  const hasSubject = computed(() => (form.subject as string ?? '').trim().length > 0);
  const canSubmit = computed(
    () => hasContent.value && hasSubject.value && !isSubmitting.value
  );

  const currentExpiryLabel = computed(
    () =>
      lifetimeOptions.value.find((o) => o.value === form.ttl)?.label ??
      t('web.secrets.selectDuration')
  );

  // Recipient row follows the same capability gate as SecretForm: an unset
  // flag means enabled (the config default). When email delivery is not
  // available, the composer only ever mints a link.
  const showRecipient = computed(() => uiCapabilities.value?.recipient !== false);

  // Delivery mode. Channels matching the receipt's share options:
  //   • link     — just mint a share link (default).
  //   • email    — also ask the backend to notify a recipient server-side.
  //   • whatsapp — create, then open wa.me with the number + link (client-side).
  //   • telegram — create, then open Telegram's share intent (client-side).
  // The recipient field is only shown/submitted in "email" mode, so a plain
  // link never carries an address. The chosen method is recorded on the form so
  // it lands on the receipt (ENVIO column).
  //
  // Telegram is temporarily DISABLED (removed from shareChannels) while the
  // delivery approach is still being decided — the type/submit/note plumbing
  // is left in place so re-enabling is just adding its entry back below.
  type ShareType = 'link' | 'email' | 'whatsapp' | 'telegram';
  const shareType = ref<ShareType>('link');

  const shareChannels: { type: ShareType; label: string; testid: string }[] = [
    { type: 'link', label: 'dashboard.share_via_link', testid: 'dashboard-type-link' },
    { type: 'email', label: 'dashboard.share_via_email', testid: 'dashboard-type-email' },
    { type: 'whatsapp', label: 'dashboard.share_via_whatsapp', testid: 'dashboard-type-whatsapp' },
  ];

  const setShareType = (type: ShareType) => {
    if (shareType.value === type) return;
    shareType.value = type;
    operations.updateField('delivery_method', type);
    // Leaving email mode: drop any typed recipient so the server doesn't email
    // an address the user changed their mind about.
    if (type !== 'email') {
      operations.updateField('recipient', '');
    }
    if (type === 'email') {
      nextTick(() => recipientRef.value?.focus());
    } else if (type === 'whatsapp') {
      nextTick(() => waNumberRef.value?.focus());
    }
  };

  // ASSUNTO (required): a subject line that identifies the secret in the
  // messages ledger. It is an internal label only — it never sets the email
  // Subject: header (that stays the platform-standard localized subject).
  const subjectValue = computed({
    get: () => (form.subject as string) ?? '',
    set: (value: string) => operations.updateField('subject', value),
  });

  // ── WhatsApp channel (client-side wa.me hand-off; Brazil +55 pre-selected) ──
  const selectedCountry = ref<CountryCode>(DEFAULT_COUNTRY);
  const waNumber = ref('');
  const waNumberRef = ref<HTMLInputElement | null>(null);
  const countryOpen = ref(false);
  const countrySelect = ref<HTMLElement | null>(null);
  onClickOutside(countrySelect, () => {
    countryOpen.value = false;
  });
  const pickCountry = (country: CountryCode) => {
    selectedCountry.value = country;
    countryOpen.value = false;
  };

  const isDesktop = useMediaQuery('(min-width: 640px)');
  const isMac = computed(
    () => typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform)
  );

  // Thousands separators follow the active UI locale (10.000 in pt_BR).
  const numberLocale = computed(() => String(locale.value ?? 'en').replace('_', '-'));
  const formatCount = (value: number) => value.toLocaleString(numberLocale.value);

  const handleSubmit = () => {
    if (!canSubmit.value) return;
    // Persist the WhatsApp destination number on the receipt (ledger only — the
    // send stays a client-side wa.me hand-off). Cleared for other channels so a
    // stale number never sticks to a later link/email secret.
    const phone =
      shareType.value === 'whatsapp' && waNumber.value.trim()
        ? `+${selectedCountry.value.dial}${waNumber.value.replace(/\D/g, '')}`
        : '';
    operations.updateField('recipient_phone', phone);
    return submit('conceal');
  };

  const onShortcut = (event: KeyboardEvent) => {
    if (!(event.ctrlKey || event.metaKey) || event.key !== 'Enter') return;
    event.preventDefault();
    handleSubmit();
  };

  const shareUrl = computed(() => {
    const receipt = lastReceipt.value;
    if (!receipt) return '';
    const domain = receipt.shareDomain || linkHost.value;
    // Append the E2EE attachment fragment (kept in sessionStorage) so the copied
    // link carries the keys the recipient needs to decrypt the files.
    const frag = peekAttachmentFragment(receipt.receiptExtid);
    return `https://${domain}/secret/${receipt.secretExtid}${frag ? `#${frag}` : ''}`;
  });

  const { isCopied, copyToClipboard } = useClipboard();

  const resetComposer = () => {
    lastReceipt.value = null;
    textareaRef.value?.focus();
  };
</script>

<template>
  <div>
    <BasicFormAlerts
      :errors="Array.from(validation.errors.values())"
      class="sticky top-4 z-50" />

    <form
      :aria-busy="isSubmitting"
      data-testid="dashboard-composer"
      @submit.prevent="handleSubmit">
      <!--
        Editor panel. The mock's `#FFFFFF on --color-surface` hierarchy maps to
        bg-day for the body and bg-day-100 for the chrome strips (privacy header,
        title bar, recipient row); dark mode keeps the night-* scale used by the
        landing. `relative` anchors the CornerBrackets "scan frame" that borrows
        the receipt's visual language to give the composer more presence.
      -->
      <div
        class="relative border border-day-line-2 bg-day shadow-[0_2px_8px_rgba(13,21,55,0.07),0_14px_44px_rgba(13,21,55,0.09)] dark:border-white/[0.13] dark:bg-night-100 dark:shadow-[0_24px_64px_rgba(0,0,0,0.6)]">
        <CornerBrackets />

        <!-- privacy control header: the elevated security controls (TTL +
             passphrase) live at the very top of the card so they read as a
             deliberate group rather than a thin, ignorable line. -->
        <DashboardPrivacyBar
          :options="lifetimeOptions"
          :ttl="(form.ttl as number)"
          :passphrase="(form.passphrase as string)"
          @update:ttl="onTtlChange"
          @update:passphrase="updatePassphrase" />

        <!-- title bar -->
        <div
          class="flex h-9 items-center justify-between gap-3 border-b border-day-line bg-day-100 px-4 dark:border-white/[0.13] dark:bg-night-200">
          <div class="flex min-w-0 items-center gap-2.5">
            <span
              class="inline-block size-[5px] shrink-0 rounded-full bg-brand-600 dark:bg-brand-500"
              aria-hidden="true"></span>
            <span
              class="truncate font-mono text-[9px] tracking-[0.1em] text-day-ink-muted uppercase dark:text-night-ink-muted">
              {{ t('dashboard.panel_label', { expiry: currentExpiryLabel }) }}
            </span>
          </div>
          <span
            class="shrink-0 font-mono text-[9px] tracking-[0.04em] tabular-nums"
            :class="
              isNearLimit
                ? 'text-red-600 dark:text-red-400'
                : 'text-day-ink-faint dark:text-night-ink-faint'
            "
            :aria-label="
              t('dashboard.characters_used', {
                used: formatCount(charCount),
                limit: formatCount(maxLength),
              })
            "
            data-testid="dashboard-char-counter">
            {{ formatCount(charCount) }}/{{ formatCount(maxLength) }}
          </span>
        </div>

        <!-- editor -->
        <textarea
          ref="textareaRef"
          v-model="secretContent"
          :maxlength="maxLength"
          :disabled="isSubmitting"
          rows="10"
          class="dashboard-field block w-full resize-none border-0 bg-transparent p-5 font-mono text-sm leading-[1.75] text-day-ink caret-brand-600 placeholder:text-day-ink-faint dark:text-night-ink dark:placeholder:text-night-ink-faint"
          :placeholder="t('web.COMMON.secret_placeholder')"
          :aria-label="t('web.secrets.enter_the_secret_content_here')"
          data-testid="dashboard-secret-input"
          @keydown="onShortcut"></textarea>

        <!-- E2EE file attachment picker (only when object storage is on). -->
        <div
          v-if="attachmentsEnabled"
          class="border-t border-day-line px-4 py-3 dark:border-white/[0.13]">
          <AttachmentPicker
            v-model="selectedFiles"
            :progress="attachmentProgress"
            :uploading="uploadingAttachments"
            :max-size-bytes="attachmentMaxSize"
            :error-message="attachmentError"
            :disabled="isSubmitting && !uploadingAttachments" />
          <!-- Retry the upload against the already-created receipt. -->
          <button
            v-if="attachmentError && retryReceiptId && !uploadingAttachments"
            type="button"
            class="mt-2 inline-flex items-center gap-1.5 rounded-md bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-brand-600 dark:bg-brand-500 dark:hover:bg-brand-400"
            data-testid="attachment-retry"
            @click="retryAttachmentUpload">
            <svg
              class="size-3.5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true">
              <path d="M23 4v6h-6M1 20v-6h6" />
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
            </svg>
            {{ t('web.homepage.attach_retry') }}
          </button>
        </div>

        <!-- delivery: pick "generate link" (default) vs "send by email"; the
             recipient field only exists in email mode. Hidden entirely when the
             recipient capability is off, leaving link-only. -->
        <div
          v-if="showRecipient"
          class="border-t border-day-line bg-day-100 px-4 py-3 dark:border-white/[0.13] dark:bg-night-200">
          <div class="flex flex-wrap items-center gap-x-3 gap-y-2">
            <span
              class="shrink-0 font-mono text-[10px] tracking-[0.06em] text-day-ink-muted uppercase dark:text-night-ink-muted">
              {{ t('dashboard.share_type_label') }}
            </span>
            <div
              class="inline-flex flex-wrap overflow-hidden border border-day-line-2 bg-day shadow-[inset_0_1px_2px_rgba(13,21,55,0.04)] dark:border-white/[0.13] dark:bg-night dark:shadow-none"
              role="group"
              :aria-label="t('dashboard.share_type_label')">
              <button
                v-for="(channel, index) in shareChannels"
                :key="channel.type"
                type="button"
                :aria-pressed="shareType === channel.type"
                class="dashboard-control flex items-center gap-1.5 px-3 py-1.5 font-mono text-[10px] tracking-[0.06em] uppercase transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-inset dark:focus-visible:ring-brand-500"
                :class="[
                  index > 0 ? 'border-l border-day-line-2 dark:border-white/[0.13]' : '',
                  shareType === channel.type
                    ? 'bg-brand-600 text-white dark:bg-brand-500'
                    : 'text-day-ink-muted hover:bg-day-200 dark:text-night-ink-muted dark:hover:bg-night-100',
                ]"
                :data-testid="channel.testid"
                @click="setShareType(channel.type)">
                <svg
                  class="size-3 shrink-0"
                  viewBox="0 0 24 24"
                  aria-hidden="true">
                  <g
                    v-if="channel.type === 'email'"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round">
                    <rect
                      x="2"
                      y="4"
                      width="20"
                      height="16"
                      rx="2" />
                    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                  </g>
                  <path
                    v-else-if="channel.type === 'whatsapp'"
                    fill="currentColor"
                    d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.885-9.885 9.885" />
                  <path
                    v-else-if="channel.type === 'telegram'"
                    fill="currentColor"
                    d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
                  <path
                    v-else
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                </svg>
                {{ t(channel.label) }}
              </button>
            </div>
          </div>

          <!-- ASSUNTO (required): identifies the secret in the messages ledger.
               Internal label only — never the email subject. -->
          <div class="mt-3 flex items-center gap-3 border-t border-day-line pt-3 dark:border-white/[0.13]">
            <label
              for="dashboard-subject"
              class="shrink-0 font-mono text-[10px] tracking-[0.06em] text-day-ink-muted uppercase dark:text-night-ink-muted">
              {{ t('dashboard.subject_label') }}
              <span class="text-brand-600 dark:text-brand-500" aria-hidden="true">*</span>
            </label>
            <div
              class="flex min-w-0 grow items-center border border-day-line-2 bg-day px-3 py-2 shadow-[inset_0_1px_2px_rgba(13,21,55,0.04)] transition-colors focus-within:border-brand-500/50 dark:border-white/[0.13] dark:bg-night dark:shadow-none">
              <input
                id="dashboard-subject"
                v-model="subjectValue"
                type="text"
                required
                maxlength="120"
                :aria-required="true"
                :placeholder="t('dashboard.subject_placeholder')"
                class="dashboard-field min-w-0 grow border-0 bg-transparent p-0 text-sm text-day-ink caret-brand-600 placeholder:text-day-ink-faint dark:text-night-ink dark:placeholder:text-night-ink-faint"
                data-testid="dashboard-subject-input" />
            </div>
          </div>

          <!-- recipient (email mode only) -->
          <div
            v-if="shareType === 'email'"
            class="mt-3 flex items-center gap-3 border-t border-day-line pt-3 dark:border-white/[0.13]">
            <label
              for="dashboard-recipient"
              class="shrink-0 font-mono text-[10px] tracking-[0.06em] text-day-ink-muted uppercase dark:text-night-ink-muted">
              {{ t('dashboard.recipient_label') }}
            </label>
            <div
              class="flex min-w-0 grow items-center border border-day-line-2 bg-day px-3 py-2 shadow-[inset_0_1px_2px_rgba(13,21,55,0.04)] transition-colors focus-within:border-brand-500/50 dark:border-white/[0.13] dark:bg-night dark:shadow-none">
              <input
                id="dashboard-recipient"
                ref="recipientRef"
                type="email"
                name="recipient[]"
                autocomplete="email"
                :value="form.recipient"
                :placeholder="t('web.COMMON.email_placeholder')"
                class="dashboard-field min-w-0 grow border-0 bg-transparent p-0 text-sm text-day-ink caret-brand-600 placeholder:text-day-ink-faint dark:text-night-ink dark:placeholder:text-night-ink-faint"
                data-testid="dashboard-recipient-input"
                @input="operations.updateField('recipient', ($event.target as HTMLInputElement).value)" />
            </div>
          </div>

          <!-- WhatsApp mode: country selector (Brazil +55 default) + number.
               The message is sent from the user's own WhatsApp after creation
               (wa.me hand-off); no paid provider is involved. -->
          <div
            v-if="shareType === 'whatsapp'"
            class="mt-3 flex flex-wrap items-center gap-3 border-t border-day-line pt-3 dark:border-white/[0.13]">
            <span
              class="shrink-0 font-mono text-[10px] tracking-[0.06em] text-day-ink-muted uppercase dark:text-night-ink-muted">
              {{ t('dashboard.recipient_label') }}
            </span>
            <div
              ref="countrySelect"
              class="relative shrink-0">
              <button
                type="button"
                data-testid="dashboard-whatsapp-country"
                :aria-label="t('receipt_terminal.share_whatsapp_country')"
                :aria-expanded="countryOpen"
                class="dashboard-control flex items-center gap-1.5 border border-day-line-2 bg-day px-2.5 py-2 font-mono text-xs text-day-ink shadow-[inset_0_1px_2px_rgba(13,21,55,0.04)] transition-colors hover:border-brand-500/40 dark:border-white/[0.13] dark:bg-night dark:text-night-ink dark:shadow-none"
                @click="countryOpen = !countryOpen">
                <span
                  class="text-sm leading-none"
                  aria-hidden="true">{{ selectedCountry.flag }}</span>
                <span class="tabular-nums">+{{ selectedCountry.dial }}</span>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  class="size-3 shrink-0 opacity-60"
                  aria-hidden="true">
                  <path d="m6 9 6 6 6-6" />
                </svg>
              </button>
              <ul
                v-if="countryOpen"
                data-testid="dashboard-whatsapp-country-list"
                class="absolute top-full left-0 z-30 mt-1 max-h-56 w-60 overflow-y-auto border border-day-line-2 bg-day py-1 shadow-lg dark:border-white/[0.13] dark:bg-night-100">
                <li
                  v-for="country in COUNTRY_CODES"
                  :key="country.iso">
                  <button
                    type="button"
                    class="flex w-full items-center gap-2 px-3 py-1.5 text-left font-mono text-xs text-day-ink transition-colors hover:bg-day-200 dark:text-night-ink dark:hover:bg-night-200"
                    :class="country.iso === selectedCountry.iso ? 'bg-day-200 dark:bg-night-200' : ''"
                    @click="pickCountry(country)">
                    <span
                      class="text-sm leading-none"
                      aria-hidden="true">{{ country.flag }}</span>
                    <span class="min-w-0 flex-1 truncate">{{ country.name }}</span>
                    <span class="text-day-ink-faint tabular-nums dark:text-night-ink-faint">+{{ country.dial }}</span>
                  </button>
                </li>
              </ul>
            </div>
            <div
              class="flex min-w-0 grow items-center border border-day-line-2 bg-day px-3 py-2 shadow-[inset_0_1px_2px_rgba(13,21,55,0.04)] transition-colors focus-within:border-brand-500/50 dark:border-white/[0.13] dark:bg-night dark:shadow-none">
              <input
                ref="waNumberRef"
                v-model="waNumber"
                type="tel"
                inputmode="tel"
                :placeholder="t('receipt_terminal.share_whatsapp_number')"
                class="dashboard-field min-w-0 grow border-0 bg-transparent p-0 text-sm text-day-ink caret-brand-600 placeholder:text-day-ink-faint dark:text-night-ink dark:placeholder:text-night-ink-faint"
                data-testid="dashboard-whatsapp-number" />
            </div>
          </div>

          <!-- Telegram mode: no extra input; the share intent opens after
               creation. A short note keeps the behaviour honest. -->
          <p
            v-else-if="shareType === 'telegram'"
            class="mt-3 border-t border-day-line pt-3 font-mono text-[10px] text-day-ink-faint dark:border-white/[0.13] dark:text-night-ink-faint"
            data-testid="dashboard-telegram-note">
            {{ t('dashboard.telegram_note') }}
          </p>
        </div>

        <!-- action bar -->
        <div
          class="flex flex-wrap items-center justify-between gap-3 border-t border-day-line px-4 py-3 dark:border-white/[0.13]">
          <!--
            The mock keeps the generated link and the "new secret" action on a
            SINGLE row (an inner flex:1 container), so the wrapping of the outer
            action bar must not separate them.
          -->
          <template v-if="lastReceipt">
            <div class="flex min-w-0 grow flex-wrap items-center gap-3">
              <!-- basis-0 (flex-1), not grow: with `grow` the long URL is the
                   item's hypothetical size, so flex-wrap pushed the button to
                   its own line instead of sharing the row as the mock does. -->
              <div
                class="flex min-w-0 flex-1 items-center gap-2 border border-brand-600/20 bg-brand-600/5 px-3 py-2 dark:border-brand-500/25 dark:bg-brand-500/10">
                <span
                  class="min-w-0 grow truncate font-mono text-[11px] text-brand-600 dark:text-brand-500"
                  data-testid="dashboard-share-url">
                  {{ shareUrl }}
                </span>
                <button
                  type="button"
                  class="dashboard-control flex shrink-0 items-center gap-1.5 border px-2.5 py-1 font-mono text-[10px] transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-brand-600 dark:focus-visible:ring-brand-500"
                  :class="
                    isCopied
                      ? 'border-brand-600 bg-brand-600 text-white dark:border-brand-500 dark:bg-brand-500'
                      : 'border-day-line-2 text-day-ink-faint hover:bg-day-100 dark:border-white/[0.13] dark:text-night-ink-faint dark:hover:bg-night-200'
                  "
                  data-testid="dashboard-copy-link"
                  @click="copyToClipboard(shareUrl)">
                  <svg
                    class="size-3"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true">
                    <template v-if="isCopied">
                      <polyline points="20 6 9 17 4 12" />
                    </template>
                    <template v-else>
                      <rect
                        x="9"
                        y="9"
                        width="13"
                        height="13"
                        rx="1" />
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                    </template>
                  </svg>
                  {{ isCopied ? t('dashboard.copied') : t('dashboard.copy') }}
                </button>
              </div>
              <button
                type="button"
                class="dashboard-control shrink-0 border border-day-line-2 px-4 py-2 font-mono text-[10px] tracking-[0.06em] text-day-ink-muted uppercase transition-colors duration-150 hover:bg-day-100 focus-visible:ring-2 focus-visible:ring-brand-600 dark:border-white/[0.13] dark:text-night-ink-muted dark:hover:bg-night-200 dark:focus-visible:ring-brand-500"
                data-testid="dashboard-new-secret"
                @click="resetComposer">
                {{ t('dashboard.new_secret') }}
              </button>
            </div>
            <!-- Copy-now warning: an attached link cannot be recovered later. -->
            <p
              v-if="lastReceiptHasAttachment"
              class="flex w-full items-center gap-1.5 text-[11px] font-medium text-amber-700 dark:text-amber-400"
              data-testid="dashboard-attach-copy-now">
              <svg
                class="size-3.5 shrink-0"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true">
                <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <path d="M12 9v4M12 17h.01" />
              </svg>
              {{ t('web.secrets.attach_copy_now') }}
            </p>
          </template>

          <template v-else>
            <div
              v-if="isDesktop"
              class="flex items-center gap-1.5"
              aria-hidden="true">
              <kbd
                class="border border-day-line-2 bg-day-100 px-1.5 py-0.5 font-mono text-[9px] tracking-[0.04em] text-day-ink-faint dark:border-white/[0.13] dark:bg-night-200 dark:text-night-ink-faint">
                {{ isMac ? '⌘' : 'Ctrl' }}
              </kbd>
              <kbd
                class="border border-day-line-2 bg-day-100 px-1.5 py-0.5 font-mono text-[9px] tracking-[0.04em] text-day-ink-faint dark:border-white/[0.13] dark:bg-night-200 dark:text-night-ink-faint">
                Enter
              </kbd>
            </div>
            <div v-else></div>

            <button
              type="submit"
              :disabled="!canSubmit"
              class="dashboard-control flex items-center gap-2 px-5 py-2.5 font-mono text-[11px] tracking-[0.08em] uppercase transition-colors duration-200 focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2 dark:focus-visible:ring-brand-500"
              :class="
                canSubmit
                  ? 'cursor-pointer bg-brand-600 text-white hover:bg-brand-700 dark:bg-brand-500 dark:hover:bg-brand-600'
                  : 'cursor-not-allowed bg-day-200 text-day-ink-faint dark:bg-night-200 dark:text-night-ink-faint'
              "
              data-testid="dashboard-submit">
              <!-- the mock swaps the padlock for a 10px spinner while generating -->
              <span
                v-if="isSubmitting"
                class="inline-block size-2.5 shrink-0 animate-spin rounded-full border-[1.5px] border-current/30 border-t-current"
                aria-hidden="true"></span>
              <svg
                v-else
                class="size-[13px]"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true">
                <rect
                  x="3"
                  y="11"
                  width="18"
                  height="11"
                  rx="1" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              {{ isSubmitting ? t('dashboard.creating') : t('dashboard.create_link') }}
            </button>
          </template>
        </div>

        <!--
          Guarantees strip. Integrated as the card's final band (its own
          surface + top hairline) instead of a floating line below the panel,
          so the promises read as part of the product. A small lock glyph with
          a brand accent anchors it. The mock read "cifrado localmente · zero
          logs"; this deployment encrypts server-side and does keep operational
          logs, so the claims are narrowed to what is actually true (encryption
          at rest, TTL expiry, single read).
        -->
        <div
          class="flex items-center justify-center gap-2 border-t border-day-line bg-day-100 px-4 py-2.5 dark:border-white/[0.13] dark:bg-night-200"
          data-testid="dashboard-guarantees">
          <svg
            class="size-3 shrink-0 text-brand-600 dark:text-brand-500"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true">
            <rect
              x="3"
              y="11"
              width="18"
              height="11"
              rx="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
          <p
            class="text-center font-mono text-[9px] tracking-[0.06em] text-day-ink-muted dark:text-night-ink-muted">
            {{ t('dashboard.footnote', { expiry: currentExpiryLabel }) }}
          </p>
        </div>
      </div>
    </form>
  </div>
</template>

<style scoped>
  /*
   * Several reveal-page components ship a bare `:focus { outline: 2px solid
   * currentColor }` inside <style scoped>; Vue compiles that to
   * `:focus, [data-v-…]:focus`, so the unscoped half leaks app-wide. Being
   * unlayered CSS it also outranks Tailwind's `focus:outline-none` (which
   * lives in @layer utilities), so EVERY focusable element in this panel would
   * otherwise draw a hard 2px currentColor box on a plain mouse click —
   * measured on the real page, not just in theory. The mock has no focus
   * chrome at all on the editor fields (`border: none; outline: none`, the
   * blue caret is the whole affordance), so:
   *
   *  - .dashboard-field  → fields that sit flush inside the panel: kill the
   *    outline outright, and the box-shadow with it. @tailwindcss/forms puts a
   *    1px ring on every focused input/textarea from its own base layer, so
   *    simply dropping the `focus:ring-*` utilities still left a blue box
   *    drawn INSIDE the panel's box.
   *  - .dashboard-control → buttons: kill the leaked outline and let the
   *    Tailwind `focus-visible:ring-*` utilities take over, which keeps the
   *    keyboard affordance without firing on mouse clicks.
   */
  .dashboard-field:focus {
    outline: none;
    box-shadow: none;
  }

  .dashboard-control:focus {
    outline: none;
  }
</style>
